import { useState } from "react";
import { Calendar, ExternalLink, MapPin, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Reveal } from "@/components/Reveal";
import { Section, Eyebrow } from "@/components/common";
import { Countdown } from "@/components/Countdown";
import { QRCode } from "@/components/QRCode";
import { api } from "@/lib/api";
import { useContent } from "@/lib/useContent";
import { trackEvent } from "@/lib/analytics";
import { useElectionPhase } from "@/components/ElectionPhase";
import { ResultsBoard } from "@/components/ResultsBoard";

export default function Vote() {
  const { data: content } = useContent();
  const [form, setForm] = useState({ name: "", email: "", phone: "", vote_method: "", reminder: "", sms_consent: false });
  const [busy, setBusy] = useState(false);
  const { phase } = useElectionPhase();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/vote-plan", form);
      trackEvent("form_submit", { form: "vote_plan" });
      toast.success("Your plan to vote is set! We'll send you a reminder.");
      setForm({ name: "", email: "", phone: "", vote_method: "", reminder: "", sms_consent: false });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  if (!content) return <div className="h-screen" />;
  const vote = content.vote || {};
  const dates = [vote.election_day, vote.early_voting].filter(Boolean);

  return (
    <div>
      <div className="hero-gradient text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16 grid lg:grid-cols-2 gap-6 items-center">
          <div className="relative z-10">
            <div className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-blue-300 mb-3">Make Your Voice Heard</div>
            <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight">Your plan to vote</h1>
            <p className="mt-4 text-lg text-blue-100 max-w-xl leading-relaxed">Voting in District 7A is easy. Here's everything you need to make sure your voice is counted in 2026.</p>
          </div>
          <div className="relative flex justify-center lg:justify-end items-end self-stretch">
            <img src="/vote-satish.png" alt="Satish Chapagain pointing toward the viewer — your vote counts" className="relative z-10 w-full max-w-md lg:max-w-lg object-contain drop-shadow-2xl" data-testid="vote-hero-image" />
          </div>
        </div>
      </div>

      {/* COUNTDOWN / RESULTS */}
      {phase === "results" ? (
        <Section className="py-12">
          <Reveal className="hero-gradient rounded-3xl shadow-2xl px-5 sm:px-10 py-10 max-w-3xl mx-auto">
            <div data-testid="vote-results-section">
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white text-center mb-6">Thank you for voting!</h2>
              <ResultsBoard />
            </div>
          </Reveal>
        </Section>
      ) : (
      <Section className="py-12">
        <Reveal className="bg-gradient-to-r from-blue-950 via-slate-900 to-blue-900 text-white rounded-3xl shadow-2xl px-6 sm:px-10 py-10 flex flex-col lg:flex-row items-center justify-between gap-8">
          <div className="text-center lg:text-left">
            <div className="text-xs font-semibold uppercase tracking-wider text-blue-300">Countdown to Election Day</div>
            <h2 className="font-serif text-2xl sm:text-3xl font-bold mt-2">November 3, 2026</h2>
          </div>
          <Countdown target={content.election_day} light />
        </Reveal>
      </Section>
      )}

      {/* KEY DATES */}
      <Section className="py-6">
        <Reveal className="text-center max-w-2xl mx-auto mb-8">
          <Eyebrow>Mark Your Calendar</Eyebrow>
          <h2 className="font-serif text-3xl font-bold text-navy">Key 2026 election dates</h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {dates.map((d, i) => (
            <Reveal key={d.label} delay={i * 0.08} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid={`vote-date-${i}`}>
              <Calendar className="w-8 h-8 text-brightblue" />
              <h3 className="font-serif text-lg font-bold text-navy mt-3">{d.label}</h3>
              <p className="text-brightblue font-semibold mt-1">{d.date}</p>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">{d.note}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* ELECTION LINKS */}
      <Section className="py-12">
        <Reveal className="text-center max-w-2xl mx-auto mb-8">
          <Eyebrow>Official Resources</Eyebrow>
          <h2 className="font-serif text-3xl font-bold text-navy">Maryland State Board of Elections</h2>
          <p className="text-slate-600 mt-2">All links go to the official Maryland elections website.</p>
        </Reveal>
        <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
          {(vote.links || []).map((l, i) => (
            <Reveal key={l.label} delay={i * 0.05}>
              <a href={l.url} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-between bg-white rounded-2xl border border-slate-200 shadow-sm p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all group"
                data-testid={`vote-link-${i}`}>
                <span className="font-semibold text-navy group-hover:text-brightblue">{l.label}</span>
                <ExternalLink className="w-5 h-5 text-slate-400 group-hover:text-brightblue" />
              </a>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* PLAN TO VOTE FORM */}
      <Section className="py-12">
        <div className="grid lg:grid-cols-5 gap-10 items-start">
          <Reveal className="lg:col-span-3">
            <div className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-sm">
              <Eyebrow>Make Your Plan</Eyebrow>
              <h2 className="font-serif text-2xl font-bold text-navy">Commit to vote &amp; get a reminder</h2>
              <form onSubmit={submit} className="mt-6 space-y-4" data-testid="vote-plan-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input required placeholder="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="voteplan-name" />
                  <Input type="email" required placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="voteplan-email" />
                  <Input type="tel" placeholder="Mobile phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="voteplan-phone" />
                  <Select value={form.vote_method} onValueChange={(v) => setForm({ ...form, vote_method: v })}>
                    <SelectTrigger data-testid="voteplan-method"><SelectValue placeholder="How will you vote?" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="In person on Election Day">In person on Election Day</SelectItem>
                      <SelectItem value="Early voting">Early voting</SelectItem>
                      <SelectItem value="Mail-in ballot">Mail-in ballot</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Select value={form.reminder} onValueChange={(v) => setForm({ ...form, reminder: v })}>
                  <SelectTrigger data-testid="voteplan-reminder"><SelectValue placeholder="When should we remind you?" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1 week before">1 week before</SelectItem>
                    <SelectItem value="1 day before">1 day before</SelectItem>
                    <SelectItem value="Morning of Election Day">Morning of Election Day</SelectItem>
                  </SelectContent>
                </Select>
                <label className="flex items-start gap-2.5 text-xs text-slate-500 leading-relaxed cursor-pointer">
                  <Checkbox checked={form.sms_consent} onCheckedChange={(v) => setForm({ ...form, sms_consent: !!v })} className="mt-0.5" data-testid="voteplan-sms-consent" />
                  <span>I consent to receive campaign reminder texts from Friends of Satish Chapagain. Msg &amp; data rates may apply. Reply STOP to opt out.</span>
                </label>
                <Button type="submit" disabled={busy} className="w-full bg-brightblue hover:bg-brightblue-hover text-white font-semibold h-11" data-testid="voteplan-submit">
                  <CheckCircle2 className="w-5 h-5 mr-2" /> I'm Voting for Satish
                </Button>
              </form>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-2">
            <div className="bg-sky/60 border border-sky-border rounded-2xl p-6 text-center">
              <Eyebrow>Share Voting Info</Eyebrow>
              <p className="text-sm text-slate-600 mb-4">Download this QR code to help neighbors find voting information.</p>
              <div className="flex justify-center">
                <QRCode data={`${window.location.origin}/vote`} label="Make Your Plan to Vote" filename="satish-vote" size={160} />
              </div>
            </div>
          </Reveal>
        </div>
      </Section>
    </div>
  );
}
